import { useEffect, useState } from "react";
import {
 Button,
 RadioGroup,
 FormControlLabel,
 Radio,
 Typography,
 useTheme,
 useMediaQuery,
 Box,
 LinearProgress,
 TextField,
 CircularProgress,
 Backdrop,
} from "@mui/material";
import { questions } from "./questions";
import axios from "axios";
import Result from "./Result";

type AnswerDetail = {
 question: string;
 answer: string;
 score: number;
 timeSpent: string; // định dạng "00:00:10"
};

interface ResultData {
 name: string;
 email: string;
 answers: AnswerDetail[];
 totalScore: number;
 duration: string;
}

type Props = {
 onFinish: (result: {
  name: string;
  email: string;
  answers: AnswerDetail[];
  totalScore: number;
 }) => void;
};

export default function Quiz({ onFinish }: Props) {
 const [current, setCurrent] = useState(0);
 const [selected, setSelected] = useState<number[]>([]);
 const [timer, setTimer] = useState(15);
 const [name, setName] = useState("");
 const [email, setEmail] = useState("");
 const [openNameForm, setOpenNameForm] = useState(true);
 const [isAnswered, setIsAnswered] = useState(false);
 const [showResults, setShowResults] = useState(false);
 const [loadingSubmit, setLoadingSubmit] = useState(false); // Thêm state loading
 const [resultData, setResultData] = useState<ResultData | null>(null);
 const [hasSubmitted, setHasSubmitted] = useState(false);
 const [completionTimes, setCompletionTimes] = useState<number[]>(
  new Array(questions.length).fill(0)
 );

 const theme = useTheme();
 const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

 useEffect(() => {
  if (!name || !email) return;
  if (timer === 0) {
   if (!isAnswered) {
    const updatedSelected = [...selected];
    updatedSelected[current] = -1; // Lưu câu trả lời trống
    setSelected(updatedSelected);

    const updatedCompletionTimes = [...completionTimes];
    updatedCompletionTimes[current] = 15;
    setCompletionTimes(updatedCompletionTimes);
   }

   if (current === questions.length - 1) {
    // ✅ Chỉ submit nếu đang ở câu cuối cùng
    if (!hasSubmitted) {
     submitAnswers();
     setHasSubmitted(true);
     setTimer(0);
     setShowResults(true);
    }
   } else {
    nextQuestion(); // 👉 Nếu chưa phải câu cuối thì chuyển tiếp
   }
  }

  if (current < questions.length && timer > 0) {
   const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
   return () => clearInterval(interval);
  }
 }, [timer, current, isAnswered, name, email]);

 const handleSelect = (index: number) => {
  const updatedSelected = [...selected];
  updatedSelected[current] = index;
  setSelected(updatedSelected);
  setIsAnswered(true); // Đánh dấu đã trả lời

  // Lưu thời gian khi người dùng trả lời
  const updatedCompletionTimes = [...completionTimes];
  updatedCompletionTimes[current] = 15 - timer; // Thời gian hoàn thành = tổng thời gian trừ đi thời gian còn lại
  setCompletionTimes(updatedCompletionTimes);
 };

 const nextQuestion = () => {
  if (current < questions.length - 1) {
   setCurrent(current + 1);
   setTimer(15); // Reset timer for next question
   setIsAnswered(false); // Đặt lại trạng thái câu trả lời
  } else {
   submitAnswers(); // Auto submit when all questions are done
  }
 };

 const formatTime = (seconds: number): string => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
 };

 const submitAnswers = async () => {
  const answers = selected.map((idx, i) => ({
   question: questions[i].question, // hoặc `questions[i].questionText` nếu bạn đặt tên khác
   answer: questions[i].options[idx] || "",
   score: idx != null && idx >= 0 ? questions[i].points[idx] : 0,
   timeSpent: formatTime(completionTimes[i]), // hàm chuyển từ giây -> "HH:mm:ss"
  }));

  const totalScore = answers.reduce((sum, item) => sum + item.score, 0);
  const totalDurationSeconds = answers.reduce((sum, ans) => {
   const [hh, mm, ss] = ans.timeSpent.split(":").map(Number);
   return sum + hh * 3600 + mm * 60 + ss;
  }, 0);

  const payload = {
   name: name || "Người chơi",
   email: email || "Không có email",
   answers,
   totalScore,
  };

  setLoadingSubmit(true);
  try {
   await axios.post(
    "https://script.google.com/macros/s/AKfycbxkSCd71i_U7LiyN4dJ4yxxtjW8SbX6V0v0herqZEYmwYQecp6uQZXHErFT8LguZRLZyQ/exec",
    payload,
    {
     headers: {
      "Content-Type": "text/plain;charset=utf-8",
     },
    }
   );
   setResultData({ ...payload, duration: totalDurationSeconds.toString() });
   setShowResults(true);
   setHasSubmitted(true);
  } catch (error) {
   console.error("Error submitting answers:", error);
  } finally {
   setLoadingSubmit(false);
  }
 };

 const handleStartQuiz = () => {
  if (!name || !email) {
   alert("Vui lòng nhập đầy đủ tên và email!");
   return;
  }

  // Reset toàn bộ trạng thái khi bắt đầu làm bài
  setCurrent(0);
  setSelected(new Array(questions.length).fill(-1));
  setCompletionTimes(new Array(questions.length).fill(0));
  setTimer(15);
  setIsAnswered(false);
  setOpenNameForm(false);
 };

 const progress = ((current + 1) / questions.length) * 100;

 if (showResults && resultData) {
  return <Result result={resultData} />;
 }
 if (openNameForm)
  return (
   <Box
    maxWidth={700}
    mx='auto'
    p={isMobile ? 2 : 4}
    mt={4}
    borderRadius={3}
    boxShadow={4}
    bgcolor='background.paper'
   >
    {/* Title */}
    <Typography
     variant={isMobile ? "h5" : "h4"}
     fontWeight={600}
     color='primary'
     align='center'
     mb={3}
    >
     Mini Game “Bạn thuộc kiểu người phản biện hay phản ứng?”
    </Typography>
    <TextField
     autoFocus
     margin='dense'
     id='name'
     label='Tên của bạn'
     type='text'
     fullWidth
     variant='outlined'
     value={name}
     onChange={(e) => setName(e.target.value)}
    />
    <TextField
     margin='dense'
     id='email'
     label='Email của bạn'
     type='email'
     fullWidth
     variant='outlined'
     value={email}
     onChange={(e) => setEmail(e.target.value)}
     sx={{ mt: 2 }}
    />
    {/* Start Button */}
    <Box display='flex' justifyContent='center' mt={2}>
     <Button
      variant='contained'
      onClick={handleStartQuiz}
      color='primary'
      sx={{ fontWeight: 600, textTransform: "none" }}
     >
      Bắt đầu
     </Button>
    </Box>
   </Box>
  );

 return (
  <>
   {loadingSubmit && (
    <Backdrop
     sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
     open={true}
    >
     <CircularProgress color='inherit' />
    </Backdrop>
   )}
   <Box
    maxWidth={700}
    mx='auto'
    p={isMobile ? 2 : 4}
    mt={4}
    borderRadius={3}
    boxShadow={4}
    bgcolor='background.paper'
   >
    <Box mb={3}>
     <Typography
      variant={isMobile ? "h6" : "h5"}
      fontWeight={600}
      color='primary'
     >
      Câu hỏi {current + 1} / {questions.length}
     </Typography>
     <LinearProgress
      variant='determinate'
      value={progress}
      sx={{ height: 10, borderRadius: 5, mt: 1 }}
      color='primary'
     />
    </Box>

    {/* Question */}
    <Typography
     variant={isMobile ? "body1" : "h6"}
     fontWeight={500}
     mb={2}
     color='text.primary'
    >
     {questions[current].question}
    </Typography>

    {/* Options */}
    <RadioGroup
     value={selected[current] ?? null}
     onChange={(e) => handleSelect(parseInt(e.target.value))}
    >
     {questions[current].options.map((option: any, index: number) => (
      <FormControlLabel
       key={index}
       value={index}
       control={<Radio color='primary' />}
       label={<Typography fontSize={isMobile ? 14 : 16}>{option}</Typography>}
       sx={{ mb: 1 }}
      />
     ))}
    </RadioGroup>

    {/* Timer + Button */}
    <Box
     display='flex'
     justifyContent='space-between'
     alignItems='center'
     mt={4}
     flexDirection={isMobile ? "column" : "row"}
     gap={2}
    >
     <Typography color='secondary' fontSize={isMobile ? 14 : 16}>
      ⏱️ Thời gian còn lại: {timer}s
     </Typography>
     <Button
      variant='contained'
      onClick={nextQuestion}
      size={isMobile ? "small" : "medium"}
      disabled={!isAnswered}
      sx={{
       textTransform: "none",
       fontWeight: 600,
       width: isMobile ? "100%" : "auto",
      }}
     >
      {current < questions.length - 1 ? "Tiếp theo" : "Hoàn thành"}
     </Button>
    </Box>
   </Box>
  </>
 );
}
